import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../../lib/trpc";
import { Icon } from "../ui/Icon";
import { DocChip } from "../shipment/DocChip";
import { FilePreviewModal } from "../shipment/FilePreviewModal";

/**
 * İzlenebilir ek dosya paneli (OrderAttachment) — DocChip grid + yükleme + açıklama + önizleme/indirme + silme.
 * Hedef: woId (iş emri ürün resimleri) VEYA orderId (sipariş belgeleri/toplama). Her ekte yükleyen + tarih + açıklama.
 */
type Category = "production" | "sample" | "order_doc" | "shipment" | "driver" | "receipt";

const CAT_META: Record<Category, { label: string; icon: string; cls: string }> = {
  production: { label: "Üretim", icon: "factory", cls: "b-info" },
  sample: { label: "Örnek Ürün", icon: "package", cls: "b-info" },
  order_doc: { label: "Belge", icon: "fileText", cls: "" },
  shipment: { label: "Sevkiyat", icon: "truck", cls: "b-warn" },
  driver: { label: "Sürücü", icon: "user", cls: "" },
  receipt: { label: "Mal Kabul", icon: "download", cls: "b-info" },
};

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
}

interface Props {
  /** Hedef — biri verilir. woId → listByWo; orderId → listByOrder; lotId → listByLot; goodsReceiptId → listByGoodsReceipt (çok-kalemli mal kabul fişi). */
  woId?: string;
  orderId?: string;
  lotId?: string;
  goodsReceiptId?: string;
  canEdit: boolean;
  /** Yeni yükleme kategorisi (categoryOptions verilmezse). */
  uploadCategory: Category;
  /** Yükleme öncesi kategori seçimi (örn. WO: Üretim/Örnek). */
  categoryOptions?: Array<{ value: Category; label: string }>;
  /** Toplama görünümünde kategori rozetini göster (sipariş detayı). */
  showCategory?: boolean;
  /** Verilirse panel kendi .d-card + .d-card-head bandını çizer (belirgin bölüm geçişi; başlık solda, yükleme aksiyonları sağda). */
  title?: string;
  subtitle?: string;
  /** Salt-okunur + hiç ek yoksa hiçbir şey render etme (ör. Trace drawer'ında boş lotlarda gürültü olmasın). */
  hideWhenEmpty?: boolean;
}

export function OrderAttachmentPanel({ woId, orderId, lotId, goodsReceiptId, canEdit, uploadCategory, categoryOptions, showCategory, title, subtitle, hideWhenEmpty }: Props) {
  const qc = useQueryClient();
  const byWo = !!woId;
  const byLot = !!lotId;
  const byReceipt = !!goodsReceiptId;
  // Hook'lar koşulsuz çağrılır (rules-of-hooks); yalnız hedef olan `enabled` ile fetch eder.
  const woQ = trpc.attachment.listByWo.useQuery({ woId: woId! }, { enabled: byWo, staleTime: 5_000 });
  const orderQ = trpc.attachment.listByOrder.useQuery({ orderId: orderId! }, { enabled: !byWo && !byLot && !byReceipt && !!orderId, staleTime: 5_000 });
  const lotQ = trpc.attachment.listByLot.useQuery({ lotId: lotId! }, { enabled: byLot, staleTime: 5_000 });
  const receiptQ = trpc.attachment.listByGoodsReceipt.useQuery({ goodsReceiptId: goodsReceiptId! }, { enabled: byReceipt, staleTime: 5_000 });
  const listQ = byReceipt ? receiptQ : byLot ? lotQ : byWo ? woQ : orderQ;
  const uploadMut = trpc.attachment.upload.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const removeMut = trpc.attachment.remove.useMutation({ onSuccess: () => qc.invalidateQueries(), onError: (e) => setErr(e.message) });
  const descMut = trpc.attachment.updateDescription.useMutation({ onSuccess: () => qc.invalidateQueries(), onError: (e) => setErr(e.message) });

  const fileRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [category, setCategory] = useState<Category>(categoryOptions?.[0]?.value ?? uploadCategory);

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setErr(null);
    const reader = new FileReader();
    reader.onload = () => {
      const target = byReceipt ? { goodsReceiptId } : byLot ? { lotId } : byWo ? { woId } : { orderId };
      uploadMut.mutate(
        { ...target, category: categoryOptions ? category : uploadCategory, filename: file.name, dataBase64: String(reader.result) },
        { onError: (er) => setErr(er.message) },
      );
    };
    reader.readAsDataURL(file);
  }

  const rows = listQ.data ?? [];

  // Salt-okunur + boş: hiçbir şey render etme (Trace drawer'ında eksiz lotlarda gürültü olmasın).
  if (hideWhenEmpty && !canEdit && !listQ.isLoading && rows.length === 0) return null;

  const controls = canEdit ? (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      {categoryOptions ? (
        <select className="form-select" style={{ height: 34, width: "auto", minWidth: 130 }} value={category} onChange={(e) => setCategory(e.target.value as Category)} aria-label="Resim türü">
          {categoryOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : null}
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,application/pdf" style={{ display: "none" }} onChange={onPick} />
      <button type="button" className="btn-md secondary" onClick={() => fileRef.current?.click()} disabled={uploadMut.isPending}>
        <Icon name="upload" size={14} />{uploadMut.isPending ? "Yükleniyor…" : "Resim / Belge Ekle"}
      </button>
    </div>
  ) : null;

  // Kartlar — her satırda TEK kart, satırı tam kaplar (tek sütun).
  const cards = listQ.isLoading ? (
    <div style={{ fontSize: 12, color: "var(--neutral-500)" }}>Yükleniyor…</div>
  ) : rows.length === 0 ? (
    <div style={{ padding: "14px 16px", border: "1px dashed var(--neutral-300)", borderRadius: "var(--radius-md)", background: "var(--neutral-50)", fontSize: 12.5, color: "var(--neutral-500)", textAlign: "center" }}>
      Henüz dosya yok{canEdit ? " — resim veya PDF yükleyebilirsiniz." : "."}
    </div>
  ) : (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {rows.map((a) => {
        const cat = CAT_META[a.category as Category];
        // Sevkiyat/sürücü evrakı çift-yazılı (Shipment.documents) → buradan silinmez, Sevkiyat detayından yönetilir.
        const canRemove = canEdit && a.category !== "shipment" && a.category !== "driver";
        return (
          <div key={a.id} style={{ display: "flex", gap: 12, alignItems: "flex-start", width: "100%", padding: 10, border: "1px solid var(--neutral-200)", borderRadius: "var(--radius-md)", background: "var(--white)" }}>
            <div style={{ flexShrink: 0 }}>
              <DocChip url={a.url} size={100} onOpen={setPreviewUrl} onRemove={canRemove ? () => removeMut.mutate({ id: a.id }) : undefined} busy={removeMut.isPending} isPdf={a.kind === "document"} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5, minWidth: 0, flex: 1 }}>
              {showCategory && cat ? (
                <span className={`badge-pill ${cat.cls}`} style={{ alignSelf: "flex-start" }}><Icon name={cat.icon} size={10} />{cat.label}</span>
              ) : null}
              {canEdit ? (
                <textarea
                  key={`${a.id}-${a.description ?? ""}`}
                  className="form-textarea"
                  rows={3}
                  style={{ height: 58, minHeight: 58, fontSize: 11.5, lineHeight: 1.35, padding: "5px 7px", resize: "vertical", width: "100%" }}
                  defaultValue={a.description ?? ""}
                  placeholder="Açıklama…"
                  maxLength={500}
                  onBlur={(e) => { if (e.target.value !== (a.description ?? "")) descMut.mutate({ id: a.id, description: e.target.value || null }); }}
                />
              ) : a.description ? (
                <span style={{ fontSize: 11.5, color: "var(--neutral-700)", lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }} title={a.description}>{a.description}</span>
              ) : null}
              <span style={{ fontSize: 10, color: "var(--neutral-500)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={`${a.uploadedBy ?? ""} · ${fmtDateTime(a.createdAt)}${a.fileName ? " · " + a.fileName : ""}`}>
                {a.uploadedBy ? `${a.uploadedBy} · ` : ""}{fmtDateTime(a.createdAt)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );

  const errBanner = err ? <div className="form-banner-err" style={{ marginBottom: 10 }}>{err}</div> : null;
  const modal = <FilePreviewModal url={previewUrl} onClose={() => setPreviewUrl(null)} />;

  // title verilirse: belirgin bölüm geçişi — .d-card + .d-card-head bandı (başlık solda, yükleme sağda). Referans tasarım.
  if (title) {
    return (
      <div className="d-card" style={{ marginTop: 18 }}>
        <div className="d-card-head">
          <div className="left"><span className="t">{title}</span>{subtitle ? <span className="s">{subtitle}</span> : null}</div>
          {controls}
        </div>
        <div className="d-card-body">
          {errBanner}
          {cards}
          {modal}
        </div>
      </div>
    );
  }

  // Başlıksız (sipariş detayı — dış .d-card içinde): sağa yaslı kontroller + kartlar.
  return (
    <div>
      {errBanner}
      {controls ? <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>{controls}</div> : null}
      {cards}
      {modal}
    </div>
  );
}
